'use client';
import '@eqi-investimentos/slider/css';
import type { FC, ReactElement } from 'react';
import Link from 'next/link';
import { ChevronRight } from '@eqi-investimentos/icons/web';
import { Flex, type FlexProps, Text } from '@eqi-investimentos/react';
import { Slider, type SliderProps } from '@eqi-investimentos/slider';
import { IfRender } from '@/utils/jsx';

export const WidgetContainer: FC<WidgetContainerProps> = ({
  children,
  containerId,
  buttonLink,
  buttonTitle = 'Ver tudo',
  containerProps,
  customTitle,
  containerTitleProps,
  darkMode = false,
  removeSeeAllButton = false,
  showTitles = true,
  title,
  sliderProps,
  containerSxProps = {
    '& .swiper-slide': { _last: { pr: '2xl' } },
    '& > div > div': {
      _first: { pr: '2xl' },
    },
  },
  customButtonTitle = null,
}) => (
  <Flex
    pos="relative"
    flexDir="column"
    w="full"
    h="full"
    gap="l"
    color="content.primary"
    {...containerProps}
    sx={{
      ...containerSxProps,
      ...(sliderProps?.displayControls === false && {
        [`.slide-prev-${containerId}`]: { display: 'none' },
        [`.slide-next-${containerId}`]: { display: 'none' },
      }),
    }}
  >
    <IfRender condition={!!customTitle}>
      <Flex
        pos="absolute"
        top="0.375rem"
        alignItems="center"
        gap="5xl"
        zIndex="overlay"
        {...containerTitleProps}
      >
        {customTitle}
      </Flex>
    </IfRender>
    <IfRender condition={showTitles}>
      <Flex
        pos="absolute"
        top="0.375rem"
        alignItems="center"
        gap="5xl"
        zIndex="overlay"
        {...containerTitleProps}
      >
        <Text aria-label="title-widget" size="h6" fontWeight="normal">
          {title}
        </Text>
        <IfRender condition={!removeSeeAllButton && !!buttonLink}>
          <Link href={buttonLink!} passHref>
            <Text size="l" color="content.brand">
              {buttonTitle}
              <ChevronRight boxSize="1rem" />
            </Text>
          </Link>
        </IfRender>
        <IfRender condition={!!customButtonTitle}>{customButtonTitle}</IfRender>
      </Flex>
    </IfRender>
    <Slider
      id={containerId}
      controlsAlignment="top-right"
      displayControls
      displayProgress={false}
      slidesGap={20}
      swiperGap="20px"
      variant={darkMode ? 'dark' : 'light'}
      iconStyles={{
        borderColor: 'border.primary',
        color: 'content.brand',
        _disabled: {
          color: 'action.disabled',
        },
      }}
      {...sliderProps}
    >
      {children}
    </Slider>
  </Flex>
);

type WidgetContainerProps = {
  children: ReactElement[];
  containerId: SliderProps['id'];
  buttonLink?: string | null;
  buttonTitle?: string;
  containerProps?: Omit<FlexProps, 'sx'>;
  containerTitleProps?: Omit<FlexProps, 'sx'>;
  containerSxProps?: FlexProps['sx'];
  customTitle?: ReactElement;
  customButtonTitle?: ReactElement;
  darkMode?: boolean;
  removeSeeAllButton?: boolean;
  showTitles?: boolean;
  sliderProps?: Omit<SliderProps, 'children' | 'id' | 'iconStyles'>;
  title?: string;
};
